import { NgIf } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

import { TermsAndConditionsMetadata } from '../../models/terms-and-conditions-metadata.model';
import { TermsAndConditionsAssetsService } from '../../services/terms-and-conditions-assets.service';

@Component({
  selector: 'app-terms-and-conditions',
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss'],
  imports: [NgIf, AgrI18nSharedModule],
})
export class TermsAndConditionsComponent implements OnDestroy {
  protected generalTermsAndConditions?: TermsAndConditionsMetadata[] = [];
  protected orderAndDeliveryConditions: TermsAndConditionsMetadata[] = [];
  protected otherDocuments: TermsAndConditionsMetadata[] = [];

  private readonly locationSubscription: Subscription;
  private location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);

  constructor(
    private readonly store: Store<RootState>,
    @Inject(TermsAndConditionsAssetsService) private readonly termsAndConditionsService: TermsAndConditionsAssetsService,
  ) {
    this.locationSubscription = this.location$.subscribe((location) => {
      if (location) {
        this.generalTermsAndConditions = this.getTermsAndConditions(location);
        this.orderAndDeliveryConditions = this.getOrderAndDeliveryConditions(location);
        this.otherDocuments = this.getOtherDocuments(location);
      }
    });
  }

  ngOnDestroy() {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
  }

  private getTermsAndConditions = (location: SelectedLocation): TermsAndConditionsMetadata[] => {
    const salesArea = this.getSalesArea(location);
    const termsAndConditions = this.termsAndConditionsService.getGeneralTermsAndConditions(salesArea);
    return termsAndConditions ? [termsAndConditions] : [];
  };

  private getOrderAndDeliveryConditions(location: SelectedLocation): TermsAndConditionsMetadata[] {
    const salesArea = this.getSalesArea(location);
    return salesArea ? this.termsAndConditionsService.getDeliveryConditions(salesArea) : [];
  }

  private getOtherDocuments(location: SelectedLocation): TermsAndConditionsMetadata[] {
    const salesArea = this.getSalesArea(location);
    return salesArea ? this.termsAndConditionsService.getOtherDocuments(salesArea) : [];
  }

  private getSalesArea = (location: SelectedLocation): string | null =>
    location?.salesAreas && location.salesAreas.length > 0 ? location.salesAreas[0].salesOrganization : null;
}
